import { useState } from "react";
import { supabase, emailService } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { getEnvironmentConfig } from "@/utils/environmentUtils";
import { detectEnvironment } from "@/utils/environmentUtils";
import { typecastSupabaseResult, hasProperty, safeGetProperty } from "@/utils/supabaseTypeUtils";
import { checkEmailExists } from "@/utils/authUtils";

export const useAuthMethods = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const environment = detectEnvironment();
  const config = getEnvironmentConfig(environment);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      setLoading(true);
      console.log(`🔍 AUTH: Signing up with email: ${email}`);
      console.log(`🔍 AUTH: Metadata:`, { firstName, lastName });
      
      // First check if the email already exists
      const emailExists = await checkEmailExists(email);
      if (emailExists) {
        console.log(`🔍 AUTH: Email already exists, aborting signup: ${email}`);
        throw new Error("An account with this email already exists. Please use a different email or try logging in.");
      }
      
      // Build metadata object
      const metadata: Record<string, any> = {};
      if (firstName) metadata.first_name = firstName;
      if (lastName) metadata.last_name = lastName;
      
      const redirectUrl = `${config.siteUrl}/dashboard`;
      console.log(`🔍 AUTH: Redirect URL: ${redirectUrl}`);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        console.error("🔍 AUTH ERROR: Supabase signup failed:", error.message);
        throw error;
      }
      
      console.log("🔍 AUTH: Supabase signup result:", {
        userId: data?.user?.id,
        emailConfirmed: data?.user?.email_confirmed_at,
        hasSession: !!data.session
      });
      
      if (data?.user && !data.session) {
        // Email confirmation is required
        console.log("🔍 AUTH: Email confirmation required");
        
        // For debugging - let's try to manually send an email
        try {
          console.log("🔍 AUTH: Attempting to send confirmation email directly via edge function");
          const emailHtml = `
            <h1>Welcome to LeadExtract!</h1>
            <p>Hello${firstName ? ' ' + firstName : ''},</p>
            <p>Thank you for signing up. Please confirm your email address by clicking the link in the email you received from Supabase.</p>
            <p>Best regards,<br>The LeadExtract Team</p>
          `;
          
          const { data: emailData, error: emailError } = await emailService.sendEmail({
            to: email,
            subject: "Welcome to LeadExtract",
            html: emailHtml,
            from: "LeadExtract <leads@notifications.leadextract.io>"
          });
          
          if (emailError) {
            console.error("🔍 AUTH ERROR: Failed to send welcome email:", emailError);
          } else {
            console.log("🔍 AUTH: Welcome email sent successfully:", emailData);
          }
        } catch (emailSendError) {
          console.error("🔍 AUTH ERROR: Exception sending welcome email:", emailSendError);
          // Don't throw here, we'll still show the confirmation message
        }
        
        toast.success("Please check your email to confirm your account");
        return data.user;
      }
      
      console.log("🔍 AUTH: Sign up successful, user is immediately logged in");
      return data.user;
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error during sign up:", error.message, error.stack);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log(`🔍 AUTH: Signing in with email: ${email}`);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("🔍 AUTH ERROR: Error during sign in:", error.message, error.stack);
        throw error;
      }
      
      if (data.user) {
        try {
          await processTeamInvitations(data.user.id, email);
        } catch (inviteError) {
          console.error("🔍 AUTH ERROR: Error processing team invitations:", inviteError);
        }
      }

      return data;
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error during sign in:", error.message, error.stack);
      
      // Enhance error messages for better user feedback
      if (error.message?.toLowerCase().includes("invalid login") || 
          error.message?.toLowerCase().includes("invalid credentials")) {
        throw new Error("Invalid email or password. Please try again.");
      } else if (error.message?.includes("Email not confirmed")) {
        throw new Error("Please check your email to confirm your account before logging in.");
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      console.log("🔍 AUTH: Initiating Google sign in");
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `https://app.leadextract.io/dashboard`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent'
          }
        }
      });

      if (error) {
        throw error;
      }

      return data;
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error during Google sign in:", error.message, error.stack);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log("🔍 AUTH: Signing out");
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("🔍 AUTH ERROR: Error during sign out:", error.message, error.stack);
        throw error;
      }
      
      console.log("🔍 AUTH: Sign out successful");
      navigate("/login");
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error during sign out:", error.message, error.stack);
      throw error;
    }
  };

  const processTeamInvitations = async (userId: string, email: string) => {
    console.log(`🔍 AUTH: Checking team invitations for user ${userId} with email ${email}`);
    
    try {
      const { data: directInvitations, error: directError } = await supabase
        .from('team_members')
        .select('*')
        .eq('user_id', userId as any)
        .eq('status', 'pending' as any);
      
      if (directError) {
        console.error("🔍 AUTH ERROR: Error fetching direct invitations:", directError);
      } else if (directInvitations && directInvitations.length > 0) {
        console.log(`🔍 AUTH: Found ${directInvitations.length} direct invitations`);
        
        for (const invite of directInvitations) {
          const typedInvite = typecastSupabaseResult<{ id: string }>(invite);
          
          if (!hasProperty(typedInvite, 'id')) {
            console.error("🔍 AUTH ERROR: Invitation missing ID:", invite);
            continue;
          }
          
          const { error: updateError } = await supabase
            .from('team_members')
            .update({
              user_id: userId,
              status: 'active',
              invite_token: null,
              updated_at: new Date().toISOString()
            } as any)
            .eq('id', typedInvite.id);
          
          if (updateError) {
            console.error(`🔍 AUTH ERROR: Error activating invitation ${typedInvite.id}:`, updateError);
          }
        }
      }
      
      const { data: emailInvitations, error: emailError } = await supabase
        .from('team_members')
        .select('*')
        .eq('email', email as any)
        .eq('status', 'pending' as any);
      
      if (emailError) {
        console.error("🔍 AUTH ERROR: Error fetching email invitations:", emailError);
      } else if (emailInvitations && emailInvitations.length > 0) {
        console.log(`🔍 AUTH: Found ${emailInvitations.length} email invitations`);
        
        let successCount = 0;
        
        for (const invite of emailInvitations) {
          const typedInvite = typecastSupabaseResult<{ id: string }>(invite);
          
          if (!hasProperty(typedInvite, 'id')) {
            console.error("🔍 AUTH ERROR: Invitation missing ID:", invite);
            continue;
          }
          
          try {
            const { error: updateError } = await supabase
              .from('team_members')
              .update({
                user_id: userId,
                status: 'active',
                invite_token: null,
                updated_at: new Date().toISOString()
              } as any)
              .eq('id', typedInvite.id);
            
            if (!updateError) {
              successCount++;
            } else {
              console.error(`🔍 AUTH ERROR: Error activating invitation ${typedInvite.id}:`, updateError);
            }
          } catch (error) {
            const inviteId = hasProperty(invite, 'id') ? safeGetProperty<string>(invite, 'id') : 'unknown';
            console.error(`🔍 AUTH ERROR: Exception activating invitation ${inviteId}:`, error);
          }
        }
        
        if (successCount > 0) {
          toast.success(`You've been added to ${successCount} team${successCount > 1 ? 's' : ''}`);
        }
      } else {
        console.log("🔍 AUTH: No pending invitations found");
      }
    } catch (error) {
      console.error("🔍 AUTH ERROR: Error processing team invitations:", error.message, error.stack);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${config.siteUrl}/reset-password`,
      });

      if (error) {
        throw error;
      }
      
      return true;
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error during password reset:", error.message, error.stack);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async (password: string) => {
    try {
      setLoading(true);
      
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) {
        throw error;
      }
      
      return true;
    } catch (error: any) {
      console.error("🔍 AUTH ERROR: Error updating password:", error.message, error.stack);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    loading
  };
};
